import React, { Fragment, memo, useMemo } from 'react'
import tw from 'twin.macro'
import { Outlet, useParams } from 'react-router-dom'
import { GSIcon } from 'src/components/shared/GSIcon'
import { SideNav } from 'src/components/layout/SideNav'
import { SideNavGroup } from 'src/components/layout/SideNavGroup'
import { SideNavItem } from 'src/components/layout/SideNavItem'
import { useInternalPermissions } from 'src/hooks/useInternalPermissions'
import {
  filterByPermission,
  filterSectionsByPermission,
} from 'src/utils/internalAccess/filterByPermission'
import {
  ADMIN_DASHBOARD_CUSTOMER_OVERVIEW,
  ADMIN_DASHBOARD_CUSTOMER_TABS,
  ADMIN_DASHBOARD_GLOBAL_SECTIONS,
  buildCustomerOverviewPath,
  buildCustomerTabPath,
} from './adminDashboardNav'

const AdminDashboardLayoutComponent = () => {
  const { customerId } = useParams<{ customerId: string }>()
  const permissions = useInternalPermissions()

  // Rail entries mirror the route guards in AppRoutes: a hidden entry is also a "No access" route.
  const visibleCustomerTabs = useMemo(() => {
    return filterByPermission(ADMIN_DASHBOARD_CUSTOMER_TABS, permissions)
  }, [permissions])

  const visibleGlobalSections = useMemo(() => {
    return filterSectionsByPermission(ADMIN_DASHBOARD_GLOBAL_SECTIONS, permissions)
  }, [permissions])

  return (
    <Fragment>
      <SideNav>
        {customerId ? (
          <Fragment>
            <SideNavGroup label="Customer setup">
              <SideNavItem
                to={buildCustomerOverviewPath(customerId)}
                label={ADMIN_DASHBOARD_CUSTOMER_OVERVIEW.label}
                icon={<GSIcon iconName={ADMIN_DASHBOARD_CUSTOMER_OVERVIEW.iconName} size={20} />}
                matchExact
              />
              {visibleCustomerTabs.map((tab) => {
                return (
                  <SideNavItem
                    key={tab.slug}
                    to={buildCustomerTabPath(customerId, tab.slug)}
                    label={tab.label}
                    icon={<GSIcon iconName={tab.iconName} size={20} />}
                  />
                )
              })}
            </SideNavGroup>
          </Fragment>
        ) : (
          visibleGlobalSections.map((section) => {
            return (
              <SideNavGroup key={section.label} label={section.label}>
                {section.items.map((item) => {
                  return (
                    <SideNavItem
                      key={item.to}
                      to={item.to}
                      label={item.label}
                      icon={<GSIcon iconName={item.iconName} size={20} />}
                    />
                  )
                })}
              </SideNavGroup>
            )
          })
        )}
      </SideNav>
      <div css={tw`flex min-w-0 flex-1 flex-col`}>
        <Outlet />
      </div>
    </Fragment>
  )
}

export const AdminDashboardLayout = memo(AdminDashboardLayoutComponent)
